import { IHttpResponse, IHttpStatus } from '@partners/api';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { TSeverityName } from '../model';


type TSeverityCallback<T> = (result: T, status: IHttpStatus) => void;

export const matchSeverity = <T>(response: IHttpResponse<T>, severityType: TSeverityName, code?: string | string[]) => {
  const { status } = response;
  const { messageCode } = status;

  if (code) {
    return messageCode === code;
  }

  // if a specific severity code was not passed in via "code" arg, then we match
  // the severity type instead of a specific severity code
  const firstChar = messageCode.charAt(0);
  switch (severityType) {
    case 'success': {
      return firstChar === 'S';
    }
    case 'warning': {
      return firstChar === 'W';
    }
    case 'information': {
      return firstChar === 'I';
    }
    case 'error': {
      return firstChar === 'E';
    }
    case 'fatal': {
      return firstChar === 'F';
    }
    default: {
      return false;
    }
  }
};

export function tapSuccess<T>(callback?: TSeverityCallback<T>, code?: string) {
  return tapSeverity<T>('success', code, callback);
}

export function tapInformation<T>(callback?: TSeverityCallback<T>, code?: string) {
  return tapSeverity<T>('information', code, callback);
}

export function tapWarning<T>(callback?: TSeverityCallback<T>, code?: string) {
  return tapSeverity<T>('warning', code, callback);
}

export function tapError<T>(callback?: TSeverityCallback<T>, code?: string) {
  return tapSeverity<T>('error', code, callback);
}

export function tapFatal<T>(callback?: TSeverityCallback<T>, code?: string) {
  return tapSeverity<T>('fatal', code, callback);
}

export function tapSeverity<T>(severityType: TSeverityName, code?: string, callback?: TSeverityCallback<T>) {
  return (response$: Observable<IHttpResponse<T>>): Observable<IHttpResponse<T>> => {
    return response$.pipe(
      tap((response: IHttpResponse<T>) => {
        const shouldCallback = matchSeverity(response, severityType, code);
        if (callback && shouldCallback) {
          callback.call(null, response.result, response.status);
        }
      })
    );
  };
}
